import { useState, useEffect } from 'react';
import { builderApi } from '../api';
import { fmtDate, DateInput } from '../utils';

const TRADES = ['Civil', 'Structural', 'Plumbing', 'Electrical', 'Carpentry', 'Painting', 'Masonry', 'Excavation', 'Flooring', 'Waterproofing', 'HVAC', 'Other'];
const STATUSES = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD'];
const STATUS_COLORS = { PENDING: '#718096', IN_PROGRESS: '#3182ce', COMPLETED: '#38a169', ON_HOLD: '#d69e2e' };
const EMPTY = { project_id: '', project_name: '', trade: 'Civil', description: '', worker_name: '',
  work_date: new Date().toISOString().slice(0,10), status: 'IN_PROGRESS', completion_pct: 0, labour_charge: '', notes: '' };

export default function BuilderWork({ user }) {
  const isOwner = user?.role === 'role_owner';
  const [work, setWork] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterProject, setFilterProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fe, setFe] = useState({});

  const ErrBanner = ({ msg }) => msg ? (
    <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 8,
      padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 18 }}>⚠️</span>
      <span style={{ color: '#c53030', fontSize: 13, fontWeight: 600, flex: 1 }}>{msg}</span>
      <button onClick={() => setErr('')} style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: 16, padding: 0 }}>✕</button>
    </div>
  ) : null;
  const inv = (k) => fe[k] ? { borderColor: '#e53e3e', background: '#fff5f5', boxShadow: '0 0 0 3px rgba(229,62,62,.15)' } : {};

  const load = () => Promise.all([
    builderApi.getWork(filterProject || undefined),
    builderApi.getProjects(),
  ]).then(([w, p]) => { setWork(w); setProjects(p); })
    .catch(e => setErr(e.message)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line
  const reload = () => builderApi.getWork(filterProject || undefined).then(setWork).catch(e => setErr(e.message));

  const pickProject = (id) => {
    const p = projects.find(p => String(p.id) === String(id));
    setForm(f => ({ ...f, project_id: id, project_name: p?.name || '' }));
  };

  const save = async () => {
    const errors = {};
    if (!form.project_id) errors.project_id = true;
    if (!form.work_date)  errors.work_date  = true;
    setFe(errors);
    if (Object.keys(errors).length > 0) { setErr('Please fill in all required fields (marked *)'); return; }
    try {
      if (editId) await builderApi.updateWork(editId, form);
      else await builderApi.createWork(form);
      setShowForm(false); setForm(EMPTY); setEditId(null); setFe({}); load();
    } catch (e) { setErr(e.message); }
  };

  const startEdit = (w) => {
    setForm({ project_id: w.project_id || '', project_name: w.project_name || '',
      trade: w.trade || 'Civil', description: w.description || '', worker_name: w.worker_name || '',
      work_date: w.work_date?.slice(0,10) || '', status: w.status || 'IN_PROGRESS',
      completion_pct: w.completion_pct || 0, labour_charge: w.labour_charge || '', notes: w.notes || '' });
    setEditId(w.id); setShowForm(true);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this work entry?')) return;
    try { await builderApi.deleteWork(id); reload(); } catch (e) { setErr(e.message); }
  };

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="bld-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--text-primary)' }}>Work Completion</h2>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(s => !s); }}>+ Log Work</button>
      </div>

      <ErrBanner msg={err} />

      {showForm && (
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Project *</label>
              <select className="form-input" style={inv('project_id')} value={form.project_id}
                onChange={e => { pickProject(e.target.value); if (fe.project_id) setFe(p => ({ ...p, project_id: false })); }}>
                <option value="">Select project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {fe.project_id && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Trade</label>
              <select className="form-input" value={form.trade} onChange={e => f('trade', e.target.value)}>
                {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Work Date *</label>
              <DateInput className="form-input" style={inv('work_date')} value={form.work_date}
                onChange={e => { f('work_date', e.target.value); if (fe.work_date) setFe(p => ({ ...p, work_date: false })); }} />
              {fe.work_date && <div style={{ color: '#e53e3e', fontSize: 11, marginTop: 3 }}>Required</div>}
            </div>
            <div>
              <label className="form-label">Worker Name</label>
              <input className="form-input" value={form.worker_name} onChange={e => f('worker_name', e.target.value)} placeholder="Worker / contractor" />
            </div>
            <div>
              <label className="form-label">Status</label>
              <select className="form-input" value={form.status} onChange={e => f('status', e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Completion %</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="range" min="0" max="100" step="5" value={form.completion_pct}
                  onChange={e => f('completion_pct', Number(e.target.value))} style={{ flex: 1 }} />
                <span style={{ fontWeight: 700, color: '#3182ce', minWidth: 36 }}>{form.completion_pct}%</span>
              </div>
            </div>
            <div>
              <label className="form-label">Labour Charge (₹)</label>
              <input className="form-input" type="number" min="0" value={form.labour_charge} onChange={e => f('labour_charge', e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="form-label">Description</label>
              <input className="form-input" value={form.description} onChange={e => f('description', e.target.value)} placeholder="Work description" />
            </div>
            <div>
              <label className="form-label">Notes</label>
              <input className="form-input" value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Additional notes" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={save}>{editId ? 'Update' : 'Log'} Work</button>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <select className="form-input" style={{ maxWidth: 240 }} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={reload}>Filter</button>
      </div>

      {loading ? <div style={{ color: '#718096', fontSize: 13 }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Project</th><th>Trade</th><th>Worker</th><th>Description</th><th>Status</th><th>%</th><th>Labour (₹)</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {work.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#718096', padding: 24 }}>No work entries yet</td></tr>
              )}
              {work.map(w => (
                <tr key={w.id}>
                  <td>{fmtDate(w.work_date)}</td>
                  <td>{w.project_name}</td>
                  <td>{w.trade}</td>
                  <td>{w.worker_name || '—'}</td>
                  <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.description || '—'}</td>
                  <td>
                    <span style={{ background: STATUS_COLORS[w.status] || '#718096', color: '#fff',
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {w.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 40, height: 6, background: '#e2e8f0', borderRadius: 3 }}>
                        <div style={{ width: `${w.completion_pct || 0}%`, height: '100%', background: '#38a169', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#4a5568' }}>{w.completion_pct || 0}%</span>
                    </div>
                  </td>
                  <td>{w.labour_charge ? `₹${Number(w.labour_charge).toLocaleString('en-IN')}` : '—'}</td>
                  <td>
                    <button className="btn btn-outline" style={{ marginRight: 4, padding: '3px 10px', fontSize: 12 }} onClick={() => startEdit(w)}>Edit</button>
                    {isOwner && (
                      <button className="btn" style={{ background: '#fed7d7', color: '#c53030', padding: '3px 10px', fontSize: 12 }} onClick={() => del(w.id)}>Del</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
